import { memo, FC, useCallback } from 'react'
import { ChatMessage } from '@crew/models/domain'
import { AiAssistantThreadListMessageList } from './components/aiAssistantThreadListMessageList/aiAssistantThreadListMessageList'
import {
  ChatMessageEditorType,
  CrewChatMessageEditor,
} from 'components/elements/crewChatMessageEditor/crewChatMessageEditor'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { useAiAssistantService } from '@crew/states'
import { AiAssistantChannelDisplayType } from '@crew/enums/app'

type AiAssistantThreadListProps = {
  displayType: AiAssistantChannelDisplayType // AIアシスタントチャンネルの表示のタイプ（AIアシスタント画面・AIアシスタントウィンドウ）
}

/**
 * AIアシスタント - スレッドリスト表示
 */
export const AiAssistantThreadList: FC<AiAssistantThreadListProps> = memo(
  (props) => {
    // Sliceの操作を行うためのServiceを取得
    const dispatch = useAppDispatch()
    const aiAssistantService = useAiAssistantService(dispatch)

    // 表示するチャット投稿コンポーネントのタイプ()
    //  * AIアシスタント画面の場合は、RAG対象選択のコンポーネントを表示するため、AiAssistantPageを指定
    //  * AIアシスタントウィンドウの場合は、RAG対象選択のコンポーネントを表示しないため、AiAssistantWindowを指定
    const editorType =
      props.displayType === AiAssistantChannelDisplayType.AiAssistantPage
        ? ChatMessageEditorType.AiAssistantPage
        : ChatMessageEditorType.AiAssistantWindow

    // 処理対象のAIアシスタントチャンネルのチャットルーム情報をReduxから取得
    const aiAssistantChatRoom = useAppSelector(
      (state) => state.message.chat.aiAssistantChannel.chatRoom
    )

    // チャット投稿時のイベントハンドラ
    const handleChatMessageSubmit = useCallback(
      (message: ChatMessage) => {
        // 右パネルにスレッド表示をするため、トピックを設定する
        aiAssistantService.setCurrentChatThread({
          topicId: message.id,
          chatRoomId: message.chatRoomId,
          rootEntityType: message.chatRoom.rootEntityType,
          rootEntityRecordId: message.chatRoom.rootEntityRecordId,
          entityType: message.chatRoom.entityType,
          entityRecordId: message.chatRoom.entityRecordId,
        })
      },
      [aiAssistantService]
    )

    // 表示に必要なデータが指定されていない場合、表示することができないので、何も表示しない
    if (!aiAssistantChatRoom) {
      return null
    }

    return (
      <div className="flex flex-col w-full h-full gap-1 overflow-hidden">
        {/* メッセージリスト */}
        <AiAssistantThreadListMessageList
          key={aiAssistantChatRoom.id}
          onMessageEdit={handleChatMessageSubmit}
          showTipsMessageItem={
            props.displayType ===
            AiAssistantChannelDisplayType.AiAssistantWindow
          } // TipsメッセージはAIアシスタントウィンドウの場合のみ表示する
        />

        {/* チャット投稿コンポーネント */}
        <CrewChatMessageEditor
          isEditMode={false}
          chatRoomId={aiAssistantChatRoom.id}
          threadRootMessageId={undefined} // スレッドリスト形式の際はスレッド返信ではないのでundefinedを渡す
          rootEntityType={aiAssistantChatRoom.rootEntityType}
          rootEntityRecordId={aiAssistantChatRoom.rootEntityRecordId}
          canUploadFile={false}
          editorType={editorType} // props.displayTypeに応じて、RAG対象のドロップダウンの表示 / 非表示を切り替える
          onSubmit={handleChatMessageSubmit}
        />
      </div>
    )
  }
)
